import { useId, useRef, useState, type CSSProperties, type JSX } from 'react'
import { ApiError, api, type ConfigVersion } from '../api.js'
import { formatDate } from './Pill.js'
import { T } from '../tokens.js'

/**
 * *"What was the program's configuration on 12 July?"* — R-22, the wireframe's
 * **View config as of a date** control (artboard 1f), over
 * `GET /api/config/at`.
 *
 * ## Why this belongs beside the editor rather than in a history screen
 *
 * `config_versions` is append-only and effective-dated: committing never
 * overwrites, so every estimate the book has ever produced is still explicable
 * against the document that was in force when it was computed. That property is
 * invisible unless something reads it back, and the person who most needs to
 * see it is the one about to commit — the sentence *"rev 7 still reproduces
 * July's numbers"* is only credible if rev 7 is one date-entry away.
 *
 * ## It fetches on demand and never on mount
 *
 * Nothing here runs until the program manager asks a question, which is the
 * difference between a control and a fifth request in the console's mount
 * storm. It also keeps this component out of every sibling suite's
 * `onUnhandledRequest: 'error'` handler list.
 *
 * ## A date before the first version is a 404, and that is an answer
 *
 * `resolveConfigAt` refuses to name "the earliest version" as what was in force
 * before it, because it was not. The route turns that into a 404 and this reads
 * it back as a sentence rather than as a failure — asking about a date before
 * the program existed is a reasonable question with a real answer.
 */

export const NO_CONFIG_ON_DATE = 'No configuration was in force on that date — it is before the first version.'
export const TIME_TRAVEL_FAILED = 'That configuration could not be loaded. Try again.'

const microLabel: CSSProperties = {
  fontSize: T.micro.size,
  fontWeight: T.weight.semi,
  letterSpacing: T.micro.tracking,
  textTransform: T.micro.transform,
  color: T.color.micro,
}

const field: CSSProperties = {
  border: `1px solid ${T.color.border}`,
  borderRadius: T.radius.square,
  padding: '7px 8px',
  fontFamily: T.fontFamily,
  fontSize: 13.5,
  color: T.color.ink,
  background: '#fff',
}

const button: CSSProperties = {
  border: `1.5px solid ${T.color.border}`,
  background: '#fff',
  color: T.color.ink,
  padding: '8px 12px',
  fontFamily: T.fontFamily,
  fontSize: T.micro.size,
  fontWeight: T.weight.bold,
  letterSpacing: '0.08em',
  textTransform: T.micro.transform,
  borderRadius: T.radius.button,
  cursor: 'pointer',
}

type Look =
  | { phase: 'idle' }
  | { phase: 'loading' }
  | { phase: 'failed'; message: string }
  | { phase: 'found'; version: ConfigVersion; date: string }

export interface TimeTravelProps {
  /** What the date box starts on. The console passes the day it resolved its own base version against. */
  today: string
}

export function TimeTravel({ today }: TimeTravelProps): JSX.Element {
  const [date, setDate] = useState(today)
  const [look, setLook] = useState<Look>({ phase: 'idle' })
  // Which request the panel is waiting for: a program manager can change the
  // date and ask again before the first answer lands, and the slower answer is
  // not the answer to the question on screen.
  const era = useRef(0)

  const ask = (): void => {
    const mine = era.current + 1
    era.current = mine
    const asked = date
    setLook({ phase: 'loading' })
    void api.configAt(asked)
      .then((version) => {
        if (era.current === mine) setLook({ phase: 'found', version, date: asked })
      })
      .catch((error: unknown) => {
        if (era.current !== mine) return
        const missing = error instanceof ApiError && error.failure === 'not_found'
        setLook({ phase: 'failed', message: missing ? NO_CONFIG_ON_DATE : TIME_TRAVEL_FAILED })
      })
  }

  const dateId = useId()

  return (
    <div data-testid="time-travel" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label htmlFor={dateId} style={microLabel}>Configuration on</label>
          <input
            id={dateId}
            type="date"
            value={date}
            onChange={(event) => { setDate(event.target.value) }}
            style={field}
          />
        </div>
        <button type="button" onClick={ask} disabled={date === ''} style={button}>
          Look up
        </button>
      </div>

      <div aria-live="polite" data-testid="time-travel-result" data-phase={look.phase}>
        {look.phase === 'loading'
          ? <p style={{ margin: 0, fontSize: 12.5, color: T.color.micro }}>Looking…</p>
          : null}
        {look.phase === 'failed'
          ? (
            <p role="alert" style={{ margin: 0, fontSize: 12.5, color: T.color.warnInk, maxWidth: '48ch' }}>
              {look.message}
            </p>
          )
          : null}
        {look.phase === 'found'
          ? (
            <p style={{ margin: 0, fontSize: 12.5, color: T.color.body, maxWidth: '52ch' }}>
              {`On ${formatDate(look.date)}: ${look.version.label}, effective `
                + `${formatDate(look.version.effectiveFrom)}. Energy `
                + `$${look.version.document.rates.energyPerKwh}/kWh, season `
                + `${formatDate(look.version.document.season.start)} to `
                + `${formatDate(look.version.document.season.end)}.`}
            </p>
          )
          : null}
      </div>
    </div>
  )
}
